import * as React from 'react'
import * as cn from 'classnames'
import { PanelDesktop, PanelTriggerDesktop, PanelDropdown, PanelTriggerDropdown } from './Panel'
import { ProfilesDropdown, ProfilesMenu } from '../../Shared/components/ProfilesMenu'
import {
  RetailNavProps,
  RetailNavState,
  NavigationLogo,
  RetailMenuProps,
  RetailNavDropdownProps,
  PanelHooks,
  NavLink,
  RetailDropdownMenusProps
} from '../types'
import { isMoving, reduceMotion, styleForCustomLogo } from '../../utils/logo';
import { Tooltip } from '@amzn/dvui/dist/Tooltip';
import { Args, withArgs, Logger, NavigationNewItemNotification, WPSClient, RequestContext, NavigationIdentifiers } from 'DVWebNodeUtils';
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { stringifyOnClient } from 'DVWebNodeUtils'

const wrapWithBentoTooltip = (context: RequestContext, toolTipData: NavigationNewItemNotification) =>
  (wrappedContent: JSX.Element = <></>) => {
    const toolTipContent = <div>
      {toolTipData.title}
      <div>
        {toolTipData.body}
      </div>
    </div>
    const csrfToken = toolTipData.csrfToken

    const onToolipDismiss = async () => {
      await new WPSClient().SetPreferences({
        context,
        csrfToken,
        sessionBased: stringifyOnClient({
          navigationNotificationOptOut: true
        }) as WpsRuntime.atv.wps.SetSessionBasedPreferencesInput
      })
    }

    return (
      <Tooltip
        content={toolTipContent}
        dismissable={true}
        position={'bottom'}
        startOpen={true}
        onDismiss={onToolipDismiss}
        use={'info'}>
        {wrappedContent}
      </Tooltip>
    )
  }

const isIDCategories2020 = (id: NavigationIdentifiers) => id === NavigationIdentifiers.PV_NAV_CATEGORIES_2020

const isHeading = (navLink: NavLink): Boolean => navLink && navLink.features && navLink.features.includes("heading")

const MenuDropdownElement = ( { id, links, logger }: NavLink & Args) => {
  const isCategories2020 = isIDCategories2020(id)
  const categoriesAnchor = (link: NavLink) =>
    <a
      href={link.href}
      onClick={() => logger.count("categories2020ClickedMobile", 1, false) }
      className={link.isHighlighted ? 'av-panel-item-highlight' : ''} rel={link.text}>
        {link.text}
    </a>
  
  return <ul className={cn('av-retail-m-nav-panel-menu', { 'categories-2020' : isCategories2020 })} >
    {links.map((link, index) => (
      <li key={`mobile-dropdown-${index}`}
          className={isHeading(link) ? "categories-li-heading" : "categories-li"}>
        { isHeading(link) ? <h4 className="categories-heading"> {link.text} </h4> : categoriesAnchor(link) }
      </li>
    ))}
  </ul>
}
const MenuDropdown = withArgs(MenuDropdownElement)

export const RetailDropdown = ({ mainMenuLinks, subMenuLinks }: RetailMenuProps) => {
  const links = mainMenuLinks.concat(subMenuLinks as NavLink[])
  const listItems = links.map((linkProps, index) => {
    const classes = ['av-retail-m-nav-element']
    if (linkProps.isHighlighted) {
      classes.push('av-retail-m-nav-highlighted')
    }
    if (index < mainMenuLinks.length) {
      classes.push('av-retail-m-nav-main-item')
    } else {
      classes.push('av-retail-m-nav-subitem')
      if (index === mainMenuLinks.length) {
        classes.push('av-retail-m-nav-subitem-first')
      } else if (index === links.length - 1) {
        classes.push('av-retail-m-nav-subitem-last')
      }
    }

    if (mainMenuLinks && mainMenuLinks.length && linkProps.isPanel) {
      return (<PanelTriggerDropdown label={linkProps.text}>
        <PanelDropdown lists={mainMenuLinks} />
      </PanelTriggerDropdown>)
    }

    if (linkProps && linkProps.links && linkProps.links.length) {
      return (
        <PanelTriggerDropdown label={linkProps.text}>
          <MenuDropdown { ...linkProps } />
        </PanelTriggerDropdown>)
    }

    return (
      <li className={classes.join(' ')} key={`listitem-${index}`}>
        <a className="av-retail-m-nav-link" href={linkProps.href} rel={linkProps.rel}>
          {linkProps.text}
        </a>
      </li>
    )
  })

  return (
    <ul className="av-retail-m-nav-dropdown">
      {listItems}
    </ul>
  )
}

const getCategories2020 = (links: NavLink[], logger: Logger) => {
  const toListItemCurried = (liClassName: string) => (navLink: NavLink): JSX.Element => (
    <li
      key={`retaillistitem-${navLink.id}`}
      className={liClassName}
      style={navLink.imageURL ? { backgroundImage: `url(${navLink.imageURL})` } : undefined} >
      <a
        href={navLink.href}
        className="categories-a"
        onClick={() => logger.count("categories2020ClickedDesktop", 1, false)}>
        {navLink.text}
      </a>
    </li>)
  const toListItem = toListItemCurried("categories-li")
  const headings: NavLink[] = links.filter(isHeading)
  const topCategoriesHeadingIndex = links.indexOf(headings[0])
  const genresHeadingIndex = links.indexOf(headings[1])
  const otherCategoriesHeadingIndex = links.indexOf(headings[2])

  return (
  <ul className="av-retail-nav-sub-dropdown categories-2020">
    <div className='cat__wrapper'>

      <div className='cat_block cat_block--a'>
        <div className='cat_block--grid'>
          <h3>{links[topCategoriesHeadingIndex].text}</h3>
          <ul className='cat_panels' >
            {links.slice(topCategoriesHeadingIndex + 1)
              .filter(link => link.imageURL && link.imageURL.length > 0)
              .map(toListItemCurried("categories-top-li"))}
          </ul>
        </div>
      </div>

      <div className='cat_separator'></div>

      <div className='cat_block cat_block--flex'>

        <div className='cat_block--child'>
          <h3>{links[genresHeadingIndex].text}</h3>
          <ul className="cat_columns--2">
            {links
              .slice(genresHeadingIndex + 1, otherCategoriesHeadingIndex)
              .map(toListItem)}
          </ul>
        </div>

        <div className='cat_block--child'>
          <h3>{links[otherCategoriesHeadingIndex].text}</h3>
          <ul>
            {links
              .slice(otherCategoriesHeadingIndex + 1)
              .map(toListItem)}
          </ul>
        </div>

      </div>
    </div>
  </ul>)
}

const RetailListsElement = ({ context, mainMenuLinks, subMenuLinks, panelHooks, newItemNotification, logger }: RetailMenuProps & Args) => {
  const mainListItems = mainMenuLinks.map((linkProps, index) => {
    const isCategories2020 = isIDCategories2020(linkProps.id)
    const classes = ['av-retail-m-nav-main-item']
    if (linkProps.isHighlighted) {
      classes.push('av-retail-m-nav-highlighted')
    }
    let subDropdownLinks: JSX.Element[] = null
    let logCategories2020Opened: NodeJS.Timeout
    if (linkProps.links && linkProps.links.length && !linkProps.isPanel) {
      classes.push('av-retail-m-nav-sbi')
      if (isCategories2020) classes.push('av-retail-m-nav-sbi-2020')
      else subDropdownLinks = linkProps.links.map((subLink) => (
        <li key={`retaillistitem-${subLink.id}`}>
          <a
            href={subLink.href}
            className={isHeading(subLink) ? "av-retail-m-nav-categories-2020-heading" : ""}
            onClick={() => logger.count("categories2020ClickedDesktop", 1, false)}>
              {subLink.text}
          </a>
        </li>
      ))

    } else if (linkProps.isPanel) {
      return (
        <PanelTriggerDesktop
          label={linkProps.text}
          panelHooks={panelHooks}
          isHighlighted={linkProps.isHighlighted}
        />
      )
    }

    const classesSubDropdown = ['av-retail-nav-sub-dropdown']
    const mainListItem = <>
      <li
        className={classes.join(' ')}
        key={`retaillistitem-${index}`}
        onMouseLeave={() => clearTimeout(logCategories2020Opened) }
        onMouseEnter={() => logCategories2020Opened = setTimeout(() => { logger.count("categories2020OpenedDesktop", 1, false) }, 1000)}>
        <a href={(!(linkProps.links && linkProps.links.length && !linkProps.isPanel)) &&
          linkProps.href} rel={linkProps.rel}>
          {linkProps.text}
        </a>
        {isCategories2020 && getCategories2020(linkProps.links, logger)}
        {subDropdownLinks && <ul className={classesSubDropdown.join(' ')} >
          {subDropdownLinks}
        </ul>}
      </li>
      {(subDropdownLinks && newItemNotification) ?
        wrapWithBentoTooltip(context, newItemNotification)() : <></>}
    </>

    return mainListItem
  })

  const sublistItems = subMenuLinks.map((linkProps, index) => {
    const classes = ['av-retail-m-nav-subitem']
    if (linkProps.isHighlighted) {
      classes.push('av-retail-m-nav-highlighted')
    }
    return (
      <li className={classes.join(' ')} key={`sublistitem-${index}`}>
        <a href={linkProps.href} rel={linkProps.rel}>
          {linkProps.text}
        </a>
      </li>
    )
  })

  return (
    <div className="av-retail-m-nav-lists">
      <ul className="av-retail-m-nav-list-main-items">
        {mainListItems}
      </ul>
      <ul className="av-retail-m-nav-list-subitems">
        {sublistItems}
      </ul>
    </div>
  )
}
export const RetailLists = withArgs(RetailListsElement)

export class Logo extends React.Component<NavigationLogo, { isReducedMotion: boolean }> {

  constructor(props: NavigationLogo) {
    super(props)
    this.state = {
      isReducedMotion: false
    }
  }

  componentDidMount() {
    const reducedMotionObj = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.setState({ 'isReducedMotion': reducedMotionObj && reducedMotionObj.matches });
  }

  render() {
    const styledLogo = (this.state.isReducedMotion && this.props.imageURL && isMoving(this.props.imageURL)) ?
      reduceMotion(this.props.imageURL) :
      this.props.imageURL;
    return (
      <a className={styledLogo ? "av-retail-m-nav-custom-logo" : "av-retail-m-nav-text-logo"}
        style={styledLogo ? styleForCustomLogo(styledLogo) : {}}
        href={this.props.href}
      >
        {this.props.altText}
      </a>
    )
  }
}


const Dropdown = ({ context, lists, newItemNotification }: RetailNavDropdownProps) => (
  <>
    <div className="av-retail-m-nav-dropdown-selector">
      <input type="checkbox" className="av-retail-m-nav-toggle-menu"
        name="av-retail-m-nav-menu" id="av-retail-m-nav-toggle-menu" />
      <label id="av-retail-m-nav-toggle-menu-label"
        htmlFor="av-retail-m-nav-toggle-menu"></label>

      <input type="radio" className="av-retail-m-nav-close-menu"
        name="av-retail-m-nav-menu" id="av-retail-m-nav-close-menu" defaultChecked />
      <label id="av-retail-m-nav-close-menu-label"
        htmlFor="av-retail-m-nav-close-menu">{lists.close}</label>

      <input type="radio" className="av-retail-m-nav-open-menu"
        name="av-retail-m-nav-menu" id="av-retail-m-nav-open-menu" />
      <label id="av-retail-m-nav-open-menu-label"
        htmlFor="av-retail-m-nav-open-menu">{lists.open}</label>

      {newItemNotification &&
        <div className="av-retail-m-nav-dropdown-invisible">
          {wrapWithBentoTooltip(context, newItemNotification)()}
        </div>}

      <div className="av-retail-m-nav-clear"></div>
      <RetailDropdown context={context} {...lists} />
    </div>
  </>
)

export const DropdownMenus = ({ context, lists, isCerberusEnabled, newItemNotification }: RetailDropdownMenusProps) => (
  isCerberusEnabled ?
    <div className="av-retail-m-nav-dropdown-menus">
      <div className="nav-profiles">
        <ProfilesDropdown {...lists.cerberus} />
        <ol className="nav-profiles-menu">
          <ProfilesMenu {...lists.cerberus} />
        </ol>
      </div>
      <Dropdown context={context} lists={lists} newItemNotification={newItemNotification} />
    </div>
    : <Dropdown context={context} lists={lists} newItemNotification={newItemNotification} />
)

const Navigation = ({ context, logo, lists, panelHooks, isSticky, isCerberusEnabled, isKids, newItemNotification, primeFilterTooltip }: RetailNavProps) => (
  <div className={`av-retail-m-nav-container ${isCerberusEnabled ? 'av-retail-m-nav-container-with-profiles' : ''} ${isKids ? 'kids' : ''}`} data-sticky={isSticky}>
    <Logo {...logo} />
    <RetailLists context={context} {...lists} panelHooks={panelHooks} newItemNotification={newItemNotification} primeFilterTooltip={primeFilterTooltip} />
    <DropdownMenus context={context} lists={lists} isCerberusEnabled={isCerberusEnabled} newItemNotification={newItemNotification} />
  </div>
)

export class RetailNavWidget extends React.Component<RetailNavProps, RetailNavState> {
  constructor(props: RetailNavProps) {
    super(props)
    this.state = {
      panelVisible: false
    }
  }

  toggle = () => {
    this.setState({
      panelVisible: !this.state.panelVisible
    })
  }

  makeVisible = () => {
    this.setState({
      panelVisible: true
    })
  }

  makeInvisible = () => {
    this.setState({
      panelVisible: false
    })
  }

  render() {
    let panelHooks: PanelHooks = {
      changeEvent: (_: React.ChangeEvent<HTMLElement>): void => {
        this.toggle()
      },
      makeVisibleMouseEvent: (_: React.MouseEvent<HTMLElement>): void => {
        this.makeVisible()
      },
      makeInvisibleMouseEvent: (_: React.MouseEvent<HTMLElement>): void => {
        this.makeInvisible()
      },
      toggle: this.toggle
    }
    return (
      <React.Fragment>
        <Navigation {...this.props} panelHooks={panelHooks} />
        <PanelDesktop {...this.state}
          panelHooks={panelHooks}
          lists={this.props.lists.mainMenuLinks} />
      </React.Fragment>
    )
  }
}
